"use client"

import * as React from "react"
import { motion } from "motion/react"

export interface ImageGenerationProps {
  /** The actual image element to reveal */
  children: React.ReactNode
  /** Duration in ms for the reveal animation (default: 30000) */
  duration?: number
  /** If true, skip the loading animation and show image immediately */
  completed?: boolean
  /** Language for UI text: 'no' = Norwegian, 'en' = English */
  language?: "no" | "en"
}

const LABELS = {
  no: {
    starting: "Starter...",
    generating: "Genererer bilde. Kan ta et øyeblikk.",
  },
  en: {
    starting: "Getting started...",
    generating: "Creating image. May take a moment.",
  },
}

export const ImageGeneration = ({
  children,
  duration = 30000,
  completed = false,
  language = "no",
}: ImageGenerationProps) => {
  const [progress, setProgress] = React.useState(() => (completed ? 100 : 0))
  const [loadingState, setLoadingState] = React.useState<
    "starting" | "generating" | "completed"
  >(() => (completed ? "completed" : "starting"))

  // Sync when completed prop flips true
  React.useEffect(() => {
    if (completed) {
      setProgress(100)
      setLoadingState("completed")
    }
  }, [completed])

  // Progress animation while not completed
  React.useEffect(() => {
    if (completed) return

    const startingTimeout = setTimeout(() => {
      setLoadingState("generating")
      const startTime = Date.now()
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime
        const pct = Math.min(95, (elapsed / duration) * 100)
        setProgress(pct)
      }, 16)
      return () => clearInterval(interval)
    }, 3000)

    return () => clearTimeout(startingTimeout)
  }, [duration, completed])

  const labels = LABELS[language] ?? LABELS.no
  const labelText =
    loadingState === "starting"
      ? labels.starting
      : loadingState === "generating"
        ? labels.generating
        : null

  return (
    <div style={{ width: "100%", margin: "8px 0" }}>
      {/* Shimmer loading text */}
      {labelText && (
        <motion.span
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "14px",
            fontWeight: 500,
            background:
              "linear-gradient(110deg, #6A6A6A 35%, #E5E5E5 50%, #6A6A6A 75%, #6A6A6A)",
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
          initial={{ backgroundPosition: "200% 0" }}
          animate={{ backgroundPosition: "-200% 0" }}
          transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
        >
          {labelText}
        </motion.span>
      )}

      {/* 16:9 container using padding-top trick — no position:absolute needed */}
      <div
        style={{
          width: "100%",
          paddingTop: "56.25%", // 9/16 = 56.25%
          position: "relative",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #2A2A2A",
          background: "#0D0D0D",
        }}
      >
        {/* Content layer — absolutely fills the padding-top container */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          {children}
        </div>

        {/* Blur reveal overlay */}
        {loadingState !== "completed" && (
          <motion.div
            style={{
              position: "absolute",
              top: "-25%",
              left: 0,
              right: 0,
              height: "125%",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              pointerEvents: "none",
              clipPath: `polygon(0 ${progress}%, 100% ${progress}%, 100% 100%, 0 100%)`,
              maskImage:
                progress === 0
                  ? "linear-gradient(to bottom, black 0%, black 100%)"
                  : `linear-gradient(to bottom, transparent ${progress - 5}%, black ${progress + 5}%)`,
              WebkitMaskImage:
                progress === 0
                  ? "linear-gradient(to bottom, black 0%, black 100%)"
                  : `linear-gradient(to bottom, transparent ${progress - 5}%, black ${progress + 5}%)`,
            }}
          />
        )}

        {/* Progress bar */}
        {loadingState === "generating" && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              height: "2px",
              width: `${progress}%`,
              background: "linear-gradient(90deg, #1A93FE, #7C3AED)",
              transition: "width 0.1s linear",
            }}
          />
        )}
      </div>
    </div>
  )
}

ImageGeneration.displayName = "ImageGeneration"
